import { queryString } from 'helpers';
import { AppContext } from 'providers/AppContext';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import type { ContractSigner, ProcessesResponse } from 'types';

export type DrawerBusy = 'update' | 'cancel' | null;

export function useAdminDrawer(row: ProcessesResponse, onClose: () => void, onRefresh: () => void) {
	const { config, http, t } = useContext(AppContext);
	const { API } = config;
	const qs = queryString({ agreementProcessId: row.agreementProcessId });
	const { data: signers } = useSWR<ContractSigner[]>(API.getContractSigners + qs);
	const [selectedStatus, setSelectedStatus] = useState(`${row.processStatus}`);
	const [busy, setBusy] = useState<DrawerBusy>(null);
	const [confirmCancel, setConfirmCancel] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);

	const onSubmit = useCallback(() => {
		if (busy) return;
		setBusy('update');
		http
			.post(`${API.changeProcessStatus}/${row.agreementProcessId}/${selectedStatus}`, {})
			.then(() => {
				onRefresh();
				onClose();
			})
			.catch(() => setBusy(null));
	}, [API, busy, http, onClose, onRefresh, row.agreementProcessId, selectedStatus]);

	const onConfirmCancel = useCallback(() => {
		if (busy) return;
		setBusy('cancel');
		http
			.delete(`${API.deleteProcess}/${row.agreementProcessId}`)
			.then(() => {
				onRefresh();
				onClose();
			})
			.catch(() => setBusy(null));
	}, [API, busy, http, onClose, onRefresh, row.agreementProcessId]);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ('Escape' === e.key && !busy) onClose();
		};
		const onMouseDown = (e: MouseEvent) => {
			if (!busy && panelRef.current && !panelRef.current.contains(e.target as Node)) {
				onClose();
			}
		};
		document.addEventListener('keydown', onKey);
		document.addEventListener('mousedown', onMouseDown);
		return () => {
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('mousedown', onMouseDown);
		};
	}, [busy, onClose]);

	return {
		busy,
		confirmCancel,
		onConfirmCancel,
		onSubmit,
		panelRef,
		selectedStatus,
		setConfirmCancel,
		setSelectedStatus,
		signers,
		t
	};
}
